import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import LookupSelect from '../components/ui/LookupSelect';
import ContactSelect from '../components/ui/ContactSelect';
import AcquisitionTypeToggle from '../components/ui/AcquisitionTypeToggle';
import FirearmHistoryNotice from '../components/ui/FirearmHistoryNotice';
import { todayLocal } from '../lib/utils';
import SafeIcon from '../common/SafeIcon';
import { FiPlus, FiChevronDown, FiX, FiLink, FiEdit2 } from 'react-icons/fi';

export default function Acquisition() {
  const { tenant, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    manufacturer_id: '',
    importer_id: '',
    model_id: '',
    serial_number: '',
    is_non_serialized: false,
    type_id: '',
    caliber_id: '',
    acquired_from_id: '',
    acquisition_date: todayLocal(),
    acquisition_type: 'REPAIR'
  });
  const [selectedMfr, setSelectedMfr] = useState(null);
  const [linkedImporters, setLinkedImporters] = useState([]);
  const [loadingImporters, setLoadingImporters] = useState(false);
  const [allImporters, setAllImporters] = useState([]);

  // Unified importer combobox state
  const [importerOpen, setImporterOpen] = useState(false);
  const [importerSearch, setImporterSearch] = useState('');
  const [importerHighlight, setImporterHighlight] = useState(-1);
  const importerContainerRef = useRef(null);
  const importerInputRef = useRef(null);
  const importerListRef = useRef(null);

  // Create-new-importer inline form
  const [creatingImporter, setCreatingImporter] = useState(false);
  const [newImpName, setNewImpName] = useState('');
  const [newImpCity, setNewImpCity] = useState('');
  const [newImpState, setNewImpState] = useState('');
  const [savingImporter, setSavingImporter] = useState(false);

  // Notes editing
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const fetchLinkedImporters = useCallback(async () => {
    if (!selectedMfr || selectedMfr.is_domestic !== false) {
      setLinkedImporters([]);
      return;
    }
    setLoadingImporters(true);
    try {
      const data = await api.getManufacturerImporters(selectedMfr.id);
      setLinkedImporters(data);
    } catch {
      setLinkedImporters([]);
    } finally {
      setLoadingImporters(false);
    }
  }, [selectedMfr]);

  useEffect(() => { fetchLinkedImporters(); }, [fetchLinkedImporters]);

  useEffect(() => {
    if (!tenant) return;
    api.getLookups('importers', tenant.id).then(setAllImporters).catch(() => {});
  }, [tenant]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!importerOpen) return;
    const handleClick = (e) => {
      if (importerContainerRef.current && !importerContainerRef.current.contains(e.target)) {
        setImporterOpen(false);
        setImporterSearch('');
        setImporterHighlight(-1);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [importerOpen]);

  // Scroll highlight into view
  useEffect(() => {
    if (importerHighlight < 0 || !importerListRef.current) return;
    const items = importerListRef.current.querySelectorAll('[data-option]');
    if (items[importerHighlight]) items[importerHighlight].scrollIntoView({ block: 'nearest' });
  }, [importerHighlight]);

  useEffect(() => { setImporterHighlight(-1); }, [importerSearch]);

  // ── Build the unified dropdown list ──
  // Linked importers first, then a divider, then unlinked importers
  const linkedIds = new Set(linkedImporters.map(l => l.importer?.id));
  const unlinkedImporters = allImporters.filter(imp => !linkedIds.has(imp.id));

  const importerLabel = (imp) =>
    `${imp.name}${imp.city && imp.state ? ` — ${imp.city}, ${imp.state}` : ''}`;

  // Build flat list of items for the dropdown: { type, data, label }
  const buildDropdownItems = () => {
    const items = [];
    const q = importerSearch.toLowerCase();

    const filteredLinked = linkedImporters.filter(l =>
      !q || l.importer?.name?.toLowerCase().includes(q)
    );
    const filteredUnlinked = unlinkedImporters.filter(imp =>
      !q || imp.name.toLowerCase().includes(q)
    );

    filteredLinked.forEach(link => {
      items.push({
        type: 'linked',
        id: link.importer.id,
        label: importerLabel(link.importer),
        notes: link.notes,
        importer: link.importer,
      });
    });

    if (filteredLinked.length > 0 && filteredUnlinked.length > 0) {
      items.push({ type: 'divider', id: '__divider' });
    }

    filteredUnlinked.forEach(imp => {
      items.push({
        type: 'unlinked',
        id: imp.id,
        label: importerLabel(imp),
        importer: imp,
      });
    });

    return items;
  };

  const dropdownItems = importerOpen ? buildDropdownItems() : [];
  const selectableItems = dropdownItems.filter(i => i.type !== 'divider');

  // Select an importer: if linked, just set it. If unlinked, auto-link then set.
  const selectImporter = async (item) => {
    setImporterOpen(false);
    setImporterSearch('');
    setImporterHighlight(-1);

    if (item.type === 'linked') {
      setFormData(prev => ({ ...prev, importer_id: item.id }));
    } else {
      // Auto-link this importer to the manufacturer
      try {
        await api.linkManufacturerImporter(tenant.id, selectedMfr.id, item.id);
        await fetchLinkedImporters();
        setFormData(prev => ({ ...prev, importer_id: item.id }));
      } catch (err) {
        alert(`Error linking importer: ${err.message || 'Unknown error'}`);
      }
    }
  };

  const handleImporterKeyDown = (e) => {
    if (!importerOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setImporterOpen(true);
        setImporterHighlight(0);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setImporterHighlight(prev => (prev < selectableItems.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setImporterHighlight(prev => (prev > 0 ? prev - 1 : selectableItems.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (importerHighlight >= 0 && selectableItems[importerHighlight]) {
          selectImporter(selectableItems[importerHighlight]);
        } else if (selectableItems.length === 1) {
          selectImporter(selectableItems[0]);
        }
        break;
      case 'Tab':
        if (importerHighlight >= 0 && selectableItems[importerHighlight]) {
          selectImporter(selectableItems[importerHighlight]);
        } else if (selectableItems.length === 1) {
          selectImporter(selectableItems[0]);
        } else {
          setImporterOpen(false);
          setImporterSearch('');
          setImporterHighlight(-1);
        }
        break;
      case 'Escape':
        setImporterOpen(false);
        setImporterSearch('');
        setImporterHighlight(-1);
        break;
    }
  };

  const handleCreateImporter = async () => {
    if (!newImpName.trim()) return;
    setSavingImporter(true);
    try {
      const created = await api.createLookup('importers', tenant.id, newImpName.trim(), {
        city: newImpCity.trim() || null,
        state: newImpState.trim() || null,
      });
      setAllImporters(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      // Auto-link and select
      await api.linkManufacturerImporter(tenant.id, selectedMfr.id, created.id);
      await fetchLinkedImporters();
      setFormData(prev => ({ ...prev, importer_id: created.id }));
      setCreatingImporter(false);
      setNewImpName('');
      setNewImpCity('');
      setNewImpState('');
    } catch (err) {
      alert(`Error creating importer: ${err.message || 'Unknown error'}`);
    } finally {
      setSavingImporter(false);
    }
  };

  // Find selected importer display info
  const selectedImpLink = linkedImporters.find(l => l.importer?.id === formData.importer_id);
  const selectedImpObj = allImporters.find(i => i.id === formData.importer_id);
  const selectedImpLabel = selectedImpObj ? importerLabel(selectedImpObj) : '';

  // Fill the firearm fields from an earlier bound book entry for this serial.
  const applyPriorDetails = (firearm) => {
    setSelectedMfr(firearm.manufacturer || null);
    setFormData(prev => ({
      ...prev,
      manufacturer_id: firearm.manufacturer_id || '',
      importer_id: firearm.importer_id || '',
      model_id: firearm.model_id || '',
      type_id: firearm.type_id || '',
      caliber_id: firearm.caliber_id || ''
    }));
    setCreatingImporter(false);
    setEditingNotes(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tenant) {
      alert('Your user account is not associated with an FFL Tenant. Please contact an administrator.');
      return;
    }
    setLoading(true);
    try {
      await api.addAcquisition(tenant.id, user.id, formData);
      navigate('/bound-book');
    } catch (err) {
      console.error('Acquisition error:', err);
      alert(err.message || 'Error recording acquisition');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
          Acquire Firearm
        </h2>
        <Badge variant="primary">Electronic A&D Record</Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Firearm Identification</CardTitle>
          <p className="text-sm text-slate-500">Select existing lookups or create new ones on the fly.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <LookupSelect
                table="manufacturers"
                label="Manufacturer"
                value={formData.manufacturer_id}
                onChange={(val) => {
                  setFormData({...formData, manufacturer_id: val, model_id: '', importer_id: ''});
                  setCreatingImporter(false);
                  setEditingNotes(false);
                }}
                onSelectItem={(item) => setSelectedMfr(item || null)}
                required
              />

              <LookupSelect
                table="models"
                label="Model"
                value={formData.model_id}
                onChange={(val) => setFormData({...formData, model_id: val})}
                filters={{ manufacturer_id: formData.manufacturer_id }}
                createExtras={{ manufacturer_id: formData.manufacturer_id }}
                disabled={!formData.manufacturer_id}
                required
              />
            </div>

            {/* Importer section for non-domestic manufacturers */}
            {selectedMfr && selectedMfr.is_domestic === false && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="w-full">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Importer
                  </label>
                  {loadingImporters ? (
                    <div className="h-10 flex items-center text-sm text-slate-500">Loading importers...</div>
                  ) : creatingImporter ? (
                    /* ── Inline create new importer ── */
                    <div className="space-y-2 rounded-md border border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/10 p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">New Importer</span>
                        <button type="button" onClick={() => { setCreatingImporter(false); setNewImpName(''); setNewImpCity(''); setNewImpState(''); }} className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">Cancel</button>
                      </div>
                      <input
                        autoFocus
                        value={newImpName}
                        onChange={(e) => setNewImpName(e.target.value)}
                        placeholder="Importer name *"
                        className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateImporter(); } if (e.key === 'Escape') { setCreatingImporter(false); setNewImpName(''); setNewImpCity(''); setNewImpState(''); } }}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          value={newImpCity}
                          onChange={(e) => setNewImpCity(e.target.value)}
                          placeholder="City"
                          className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                        />
                        <input
                          value={newImpState}
                          onChange={(e) => setNewImpState(e.target.value)}
                          placeholder="State"
                          className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleCreateImporter}
                        disabled={!newImpName.trim() || savingImporter}
                        className="h-9 w-full inline-flex items-center justify-center gap-1 rounded-md text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
                      >
                        <SafeIcon icon={FiPlus} className="h-3.5 w-3.5" />
                        Create, Link & Select
                      </button>
                    </div>
                  ) : (
                    /* ── Unified importer combobox ── */
                    <div className="space-y-1.5">
                      <div className="flex gap-1.5" ref={importerContainerRef}>
                        <div className="relative flex-1">
                          <input
                            ref={importerInputRef}
                            type="text"
                            placeholder="Search importers..."
                            value={importerOpen ? importerSearch : selectedImpLabel}
                            onFocus={() => { setImporterOpen(true); setImporterSearch(''); setImporterHighlight(-1); }}
                            onChange={(e) => { setImporterSearch(e.target.value); if (!importerOpen) setImporterOpen(true); }}
                            onKeyDown={handleImporterKeyDown}
                            role="combobox"
                            aria-expanded={importerOpen}
                            className="flex h-10 w-full rounded-md border border-slate-300 bg-white pl-3 pr-16 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                          />
                          <div className="absolute inset-y-0 right-0 flex items-center gap-0.5 pr-2">
                            {formData.importer_id && (
                              <button type="button" tabIndex={-1} onClick={() => { setFormData(prev => ({...prev, importer_id: ''})); setImporterSearch(''); importerInputRef.current?.focus(); }} className="p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                                <SafeIcon icon={FiX} className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <SafeIcon icon={FiChevronDown} className={`h-4 w-4 text-slate-400 transition-transform ${importerOpen ? 'rotate-180' : ''}`} />
                          </div>

                          {importerOpen && (
                            <ul ref={importerListRef} className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg dark:bg-slate-900 dark:border-slate-700">
                              {(() => {
                                let selectIdx = -1;
                                return dropdownItems.length === 0 ? (
                                  <li className="px-3 py-2 text-slate-400">No importers found</li>
                                ) : (
                                  dropdownItems.map((item) => {
                                    if (item.type === 'divider') {
                                      return (
                                        <li key={item.id} className="my-1 border-t border-slate-200 dark:border-slate-700">
                                          <span className="block px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Other importers (will be linked)</span>
                                        </li>
                                      );
                                    }
                                    selectIdx++;
                                    const idx = selectIdx;
                                    const isSelected = item.id === formData.importer_id;
                                    return (
                                      <li
                                        key={item.id}
                                        data-option
                                        onClick={() => selectImporter(item)}
                                        onMouseEnter={() => setImporterHighlight(idx)}
                                        className={`cursor-pointer px-3 py-2 ${
                                          idx === importerHighlight
                                            ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-300'
                                            : isSelected
                                              ? 'bg-primary-50 dark:bg-primary-900/30 font-medium text-primary-700 dark:text-primary-400'
                                              : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                                        }`}
                                      >
                                        <span>{item.label}</span>
                                        {item.type === 'linked' && item.notes && (
                                          <span className="ml-2 text-xs text-slate-400">({item.notes})</span>
                                        )}
                                        {item.type === 'unlinked' && (
                                          <SafeIcon icon={FiLink} className="inline ml-1.5 h-3 w-3 text-slate-400" />
                                        )}
                                      </li>
                                    );
                                  })
                                );
                              })()}
                              {/* Create new option at bottom */}
                              <li
                                onClick={() => { setImporterOpen(false); setImporterSearch(''); setCreatingImporter(true); }}
                                className="cursor-pointer px-3 py-2 border-t border-slate-100 dark:border-slate-700 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 font-medium"
                              >
                                <SafeIcon icon={FiPlus} className="inline mr-1 h-3.5 w-3.5" />
                                Create new importer
                              </li>
                            </ul>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => { setImporterOpen(false); setCreatingImporter(true); }}
                          tabIndex={-1}
                          title="Create new importer"
                          className="flex items-center justify-center h-10 w-10 shrink-0 rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-primary-50 hover:text-primary-600 hover:border-primary-300 transition-colors dark:bg-slate-900 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-primary-900/30 dark:hover:text-primary-400"
                        >
                          <SafeIcon icon={FiPlus} className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Notes display/edit for selected importer */}
                      {formData.importer_id && selectedImpLink && (
                        <div className="flex items-center gap-1.5 px-1">
                          {editingNotes ? (
                            <div className="flex items-center gap-1.5 flex-1">
                              <input
                                autoFocus
                                value={notesValue}
                                onChange={(e) => setNotesValue(e.target.value)}
                                placeholder="Add notes..."
                                className="h-7 flex-1 rounded border border-slate-300 bg-white px-2 text-xs focus:ring-1 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                                onKeyDown={async (e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    setSavingNotes(true);
                                    try {
                                      await api.unlinkManufacturerImporter(selectedImpLink.id);
                                      await api.linkManufacturerImporter(tenant.id, selectedMfr.id, formData.importer_id, notesValue.trim());
                                      await fetchLinkedImporters();
                                    } catch (err) {
                                      alert(`Error saving notes: ${err.message}`);
                                    } finally {
                                      setSavingNotes(false);
                                      setEditingNotes(false);
                                    }
                                  }
                                  if (e.key === 'Escape') setEditingNotes(false);
                                }}
                              />
                              <button
                                type="button"
                                disabled={savingNotes}
                                onClick={async () => {
                                  setSavingNotes(true);
                                  try {
                                    await api.unlinkManufacturerImporter(selectedImpLink.id);
                                    await api.linkManufacturerImporter(tenant.id, selectedMfr.id, formData.importer_id, notesValue.trim());
                                    await fetchLinkedImporters();
                                  } catch (err) {
                                    alert(`Error saving notes: ${err.message}`);
                                  } finally {
                                    setSavingNotes(false);
                                    setEditingNotes(false);
                                  }
                                }}
                                className="text-xs text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
                              >
                                Save
                              </button>
                              <button type="button" onClick={() => setEditingNotes(false)} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
                            </div>
                          ) : (
                            <>
                              {selectedImpLink.notes ? (
                                <span className="text-xs text-slate-500 dark:text-slate-400">Note: {selectedImpLink.notes}</span>
                              ) : (
                                <span className="text-xs text-slate-400">No notes</span>
                              )}
                              <button
                                type="button"
                                onClick={() => { setNotesValue(selectedImpLink.notes || ''); setEditingNotes(true); }}
                                className="text-xs text-primary-500 hover:text-primary-600"
                              >
                                <SafeIcon icon={FiEdit2} className="inline h-3 w-3" />
                              </button>
                            </>
                          )}
                        </div>
                      )}

                      <input type="text" required value={formData.importer_id || ''} onChange={() => {}} tabIndex={-1} className="sr-only" aria-hidden="true" />
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    id="is_non_serialized"
                    checked={formData.is_non_serialized}
                    onChange={(e) => setFormData({
                      ...formData,
                      is_non_serialized: e.target.checked,
                      serial_number: e.target.checked ? '' : formData.serial_number
                    })}
                    className="h-4 w-4 rounded border-slate-300 text-primary-500 focus:ring-primary-500"
                  />
                  <label htmlFor="is_non_serialized" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Non-Serialized (NSN)
                  </label>
                </div>
                {formData.is_non_serialized ? (
                  <div className="flex h-10 w-full items-center rounded-md border border-slate-300 bg-slate-100 px-3 text-sm text-slate-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400">
                    NSN — No Serial Number
                  </div>
                ) : (
                  <Input
                    label="Serial Number"
                    name="serial_number"
                    value={formData.serial_number}
                    onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                    required
                    placeholder="Serial number"
                  />
                )}
              </div>
              <LookupSelect
                table="types"
                label="Firearm Type"
                value={formData.type_id}
                onChange={(val) => setFormData({...formData, type_id: val})}
                required
              />
            </div>

            {!formData.is_non_serialized && (
              <FirearmHistoryNotice
                tenantId={tenant?.id}
                serialNumber={formData.serial_number}
                onUseDetails={applyPriorDetails}
              />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <LookupSelect
                table="calibers"
                label="Caliber"
                value={formData.caliber_id}
                onChange={(val) => setFormData({...formData, caliber_id: val})}
                required
              />
              <Input
                label="Acquisition Date"
                type="date"
                name="acquisition_date"
                value={formData.acquisition_date}
                onChange={(e) => setFormData({...formData, acquisition_date: e.target.value})}
                required
              />
            </div>

            <AcquisitionTypeToggle
              value={formData.acquisition_type}
              onChange={(val) => setFormData({ ...formData, acquisition_type: val })}
            />

            <div className="border-t border-slate-100 dark:border-slate-800 pt-6">
              <h4 className="text-sm font-semibold mb-4 text-slate-900 dark:text-white">Source Contact</h4>
              <ContactSelect
                label="Acquired From"
                value={formData.acquired_from_id}
                onChange={(val) => setFormData({...formData, acquired_from_id: val})}
                required
                placeholder="Select seller / source"
              />
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                className="w-full py-6 text-lg"
                isLoading={loading}
                icon={FiPlus}
                disabled={!formData.manufacturer_id || !formData.model_id || (!formData.serial_number && !formData.is_non_serialized) || !formData.acquired_from_id}
              >
                Commit to Bound Book
              </Button>
              <p className="mt-2 text-center text-xs text-slate-500">
                Warning: This action creates a permanent federal record. Ensure accuracy before submitting.
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
